import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, MapPin, Search } from "lucide-react";
import { CITIES } from "@/data/cities";

const PLACES = CITIES.flatMap((c) => [
    { type: "city", label: c.name, sub: c.state, slug: c.slug },
    ...(c.localities || []).map((l) => ({
        type: "locality",
        label: l,
        sub: c.name,
        slug: c.slug,
    })),
]);

export default function SearchBar({
    placeholder = "Search city or locality",
    inputTestId = "search-input",
    submitTestId = "search-submit",
    bare = false,
}) {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [hi, setHi] = useState(-1);

    const results = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return PLACES.filter((p) => p.type === "city");
        return PLACES.filter((p) =>
            `${p.label} ${p.sub}`.toLowerCase().includes(needle),
        ).slice(0, 7);
    }, [q]);

    const choose = (item) => {
        setOpen(false);
        setQ("");
        setHi(-1);
        if (item.type === "city") navigate(`/city/${item.slug}`);
        else navigate(`/discover?q=${encodeURIComponent(`${item.label}, ${item.sub}`)}`);
    };

    const submit = (e) => {
        e.preventDefault();
        if (hi >= 0 && results[hi]) {
            choose(results[hi]);
            return;
        }
        setOpen(false);
        navigate(q.trim() ? `/discover?q=${encodeURIComponent(q.trim())}` : "/discover");
    };

    const onKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHi((h) => Math.min(h + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((h) => Math.max(h - 1, -1));
        } else if (e.key === "Escape") {
            setOpen(false);
            setHi(-1);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="relative flex w-full flex-col gap-3 sm:flex-row sm:items-center"
            role="search"
        >
            <div className="relative flex-1">
                <div className="flex items-center gap-3 px-4 py-2">
                    <Search className="h-4 w-4 shrink-0 text-gold" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => {
                            setQ(e.target.value);
                            setOpen(true);
                            setHi(-1);
                        }}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setTimeout(() => setOpen(false), 150)}
                        onKeyDown={onKey}
                        data-testid={inputTestId}
                        placeholder={placeholder}
                        autoComplete="off"
                        aria-label={placeholder}
                        className="w-full bg-transparent text-sm text-ivory placeholder:text-dim focus:outline-none"
                    />
                </div>
                {open && results.length > 0 && (
                    <div
                        data-testid="search-suggestions"
                        className="absolute inset-x-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border border-black/8 bg-white p-2 shadow-xl shadow-black/5"
                    >
                        {!q.trim() && (
                            <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
                                Explore cities
                            </p>
                        )}
                        {results.map((item, i) => (
                            <button
                                key={`${item.type}-${item.label}`}
                                type="button"
                                data-testid={`search-suggestion-${item.type}-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    choose(item);
                                }}
                                onMouseEnter={() => setHi(i)}
                                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                                    hi === i ? "bg-secondary" : ""
                                }`}
                            >
                                {item.type === "city" ? (
                                    <MapPin className="h-4 w-4 shrink-0 text-gold" />
                                ) : (
                                    <Building2 className="h-4 w-4 shrink-0 text-dim" />
                                )}
                                <span className="text-sm text-ivory">{item.label}</span>
                                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-dim">
                                    {item.type === "city" ? "City" : item.sub}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
                {open && q.trim() && results.length === 0 && (
                    <div
                        data-testid="search-no-match"
                        className="absolute inset-x-0 top-full z-40 mt-2 rounded-2xl border border-black/8 bg-white p-4 shadow-xl shadow-black/5"
                    >
                        <p className="text-sm text-mist">
                            No matches yet — press{" "}
                            <span className="font-medium text-ivory">Explore</span> to search
                            “{q.trim()}” anyway.
                        </p>
                    </div>
                )}
            </div>
            {!bare && (
                <button
                    type="submit"
                    data-testid={submitTestId}
                    className="rounded-full bg-gold px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-gold-bright"
                >
                    Explore
                </button>
            )}
        </form>
    );
}
